import { supabase } from '../lib/supabase';

export const agentService = {
  // Conversation operations
  async getConversations(userId, teamId = null) {
    try {
      let query = supabase?.from('agent_conversations')?.select('*')?.eq('user_id', userId);
      
      if (teamId) {
        query = query?.eq('team_id', teamId);
      }
      
      const { data, error } = await query?.order('updated_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getConversationById(conversationId) {
    try {
      const { data, error } = await supabase?.from('agent_conversations')?.select('*')?.eq('id', conversationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createConversation(userId, agentId, priority = 'normal', teamId = null) {
    try {
      const insertData = {
        user_id: userId,
        agent_id: agentId,
        priority,
        status: 'pending'
      };
      
      if (teamId) {
        insertData.team_id = teamId;
      }
      
      const { data, error } = await supabase?.from('agent_conversations')?.insert(insertData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateConversationStatus(conversationId, status) {
    try {
      const { data, error } = await supabase?.from('agent_conversations')?.update({ status })?.eq('id', conversationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Message operations
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase?.from('agent_messages')?.select('*')?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createMessage(conversationId, sender, content, deliverables = [], metadata = {}) {
    try {
      const { data, error } = await supabase?.from('agent_messages')?.insert({
          conversation_id: conversationId,
          sender,
          content,
          deliverables,
          metadata
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Task operations
  async getTasks(userId, filters = {}) {
    try {
      let query = supabase?.from('agent_tasks')?.select('*')?.eq('user_id', userId);

      if (filters?.teamId) {
        query = query?.eq('team_id', filters?.teamId);
      }
      if (filters?.agentId) {
        query = query?.eq('agent_id', filters?.agentId);
      }
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createTask(userId, agentId, taskData, teamId = null) {
    try {
      const insertData = {
        user_id: userId,
        agent_id: agentId,
        ...taskData
      };
      
      if (teamId) {
        insertData.team_id = teamId;
      }
      
      const { data, error } = await supabase?.from('agent_tasks')?.insert(insertData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTask(taskId, updates) {
    try {
      const { data, error } = await supabase?.from('agent_tasks')?.update(updates)?.eq('id', taskId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteTask(taskId) {
    try {
      const { error } = await supabase?.from('agent_tasks')?.delete()?.eq('id', taskId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Content library operations
  async getContentLibrary(userId, filters = {}) {
    try {
      let query = supabase?.from('agent_content_library')?.select('*')?.eq('user_id', userId);

      if (filters?.organizationId) {
        query = query?.eq('organization_id', filters?.organizationId);
      }
      if (filters?.agentId) {
        query = query?.eq('agent_id', filters?.agentId);
      }
      if (filters?.contentType) {
        query = query?.eq('content_type', filters?.contentType);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createContent(userId, agentId, contentData, organizationId = null) {
    try {
      const insertData = {
        user_id: userId,
        agent_id: agentId,
        ...contentData
      };
      
      if (organizationId) {
        insertData.organization_id = organizationId;
      }
      
      const { data, error } = await supabase?.from('agent_content_library')?.insert(insertData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteContent(contentId) {
    try {
      const { error } = await supabase?.from('agent_content_library')?.delete()?.eq('id', contentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Analytics operations
  async getAnalytics(userId, filters = {}) {
    try {
      let query = supabase?.from('agent_analytics')?.select('*')?.eq('user_id', userId);

      if (filters?.teamId) {
        // For analytics, we filter by organization_id through team relationship
        const { data: teamData } = await supabase?.from('teams')?.select('organization_id')?.eq('id', filters?.teamId)?.single();
        if (teamData?.organization_id) {
          query = query?.eq('organization_id', teamData?.organization_id);
        }
      }
      if (filters?.agentId) {
        query = query?.eq('agent_id', filters?.agentId);
      }
      if (filters?.metricName) {
        query = query?.eq('metric_name', filters?.metricName);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createAnalytics(userId, agentId, analyticsData) {
    try {
      const { data, error } = await supabase?.from('agent_analytics')?.insert({
          user_id: userId,
          agent_id: agentId,
          ...analyticsData
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getSmartScheduledTasks(userId, teamId = null, date = new Date()?.toISOString()?.split('T')?.[0]) {
    try {
      const { data, error } = await supabase?.rpc('get_smart_scheduled_tasks', {
        p_user_id: userId,
        p_team_id: teamId,
        p_date: date
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async autoScheduleTasks(userId, teamId = null, startDate = new Date()?.toISOString()?.split('T')?.[0], workHoursPerDay = 8) {
    try {
      const { data, error } = await supabase?.rpc('auto_schedule_tasks', {
        p_user_id: userId,
        p_team_id: teamId,
        p_start_date: startDate,
        p_work_hours_per_day: workHoursPerDay
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTaskRecommendations(userId, teamId = null, availableMinutes = 60) {
    try {
      const { data, error } = await supabase?.rpc('get_task_recommendations', {
        p_user_id: userId,
        p_team_id: teamId,
        p_current_time: new Date()?.toISOString(),
        p_available_minutes: availableMinutes
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTaskScheduling(taskId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('agent_tasks')
        ?.update({
          urgency: updates?.urgency,
          importance: updates?.importance,
          estimated_duration: updates?.estimatedDuration,
          scheduled_for: updates?.scheduledFor,
          auto_scheduled: updates?.autoScheduled,
          tags: updates?.tags
        })
        ?.eq('id', taskId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get dashboard metrics for user
   * @param {string} userId - User ID
   * @returns {Promise<{data: object, error: object}>}
   */
  async getDashboardMetrics(userId) {
    try {
      // Get tasks completed count
      const { count: completedCount, error: completedError } = await supabase?.from('agent_tasks')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('status', 'completed');

      if (completedError) throw completedError;

      // Get active tasks count
      const { count: activeCount, error: activeError } = await supabase?.from('agent_tasks')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.in('status', ['todo', 'in_progress']);

      if (activeError) throw activeError;

      // Get average efficiency from agent_usage_metrics
      const { data: metricsData, error: metricsError } = await supabase?.from('agent_usage_metrics')?.select('efficiency_score')?.eq('user_id', userId)?.not('efficiency_score', 'is', null);

      if (metricsError) throw metricsError;

      // Calculate average efficiency
      let avgEfficiency = 0;
      if (metricsData && metricsData?.length > 0) {
        const sum = metricsData?.reduce((acc, metric) => acc + parseFloat(metric?.efficiency_score || 0), 0);
        avgEfficiency = sum / metricsData?.length;
      }

      return {
        data: {
          tasks_completed: completedCount || 0,
          active_tasks: activeCount || 0,
          avg_efficiency: avgEfficiency
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      return { data: null, error };
    }
  }
};

export default agentService;